import { Component, OnInit } from "@angular/core";
import { clone } from "lodash-es";
import { StudentService } from "../../shared/services/student.service";
import { empty } from "../../functions";
import { faCalendar, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ModalService } from "../../shared/services/modal.service";
import { ModalStudentApplicationMotivationComponent } from "./modal-student-application-motivation/modal-student-application-motivation.component";
import { UserService } from "../../shared/services/user.service";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { RoleTypes } from "../../guards/roletypes";
import { catchError } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { CallForOpportunityService } from "../../api/services/call-for-opportunity.service";
import { sortAnnouncementsNewestFirst } from "../../shared/utils/call-for-opportunity.utils";

@Component({
  selector: "app-student-applications",
  templateUrl: "./student-applications.component.html",
  styleUrls: ["./student-applications.component.scss"],
  standalone: false,
})
export class StudentApplicationsComponent implements OnInit {
  currentSection = "applications";

  RoleTypes = RoleTypes;

  // Icons
  faCalendar = faCalendar;
  faTrash = faTrash;

  public view = {
    isLoading: false,
    page: 1,
    limit: 30,
    total_results: 90,
    total_pages: 1,
    filters: <any>{},
    order: "desc",
    orderby: "",
    limitOptions: [
      {
        value: 30,
        label: 30,
      },
      {
        value: 60,
        label: 60,
      },
      {
        value: 120,
        label: 120,
      },
      {
        value: 300,
        label: 300,
      },
    ],
    filterOptions: <any>{
      amount_employees: [],
      interests: [],
      jobtypes: [],
      educationlevel: [],
    },
    orderOptions: [
      {
        value: "ASC",
        label: "Old to new",
      },
      {
        value: "DESC",
        label: "New to old",
      },
    ],
    orderByOptions: [
      {
        value: "name",
        label: "Name",
      },
    ],
    data: <any>[],
  };

  public profile: any = {
    id: "",
  };

  public viewFavorites = {
    data: <any>[],
  };

  public viewAnnouncements = {
    isLoading: false,
    data: <any>[],
  };

  /**
   * Constructor
   *
   * @param studentService
   * @param userService
   * @param authService
   * @param modal
   */
  constructor(
    private studentService: StudentService,
    private userService: UserService,
    private authService: AuthenticationService,
    private modal: ModalService,
    public router: Router,
    private callForOpportunityService: CallForOpportunityService
  ) {}

  /**
   * Ng on init
   */
  ngOnInit(): void {
    if (this.router.url == "/student/jobfavorites") {
      this.currentSection = "jobfavorites";
    }

    if (this.router.url == "/student/announcements") {
      this.currentSection = "announcements";
    }

    // Get active current student profile first to load correct data
    this.studentService
      .apiGetStudentprofileCurrent()
      .pipe(
        catchError(
          this.studentService.handleError("apiGetStudentprofileCurrent", false)
        )
      )
      .subscribe((profile) => {
        if (profile !== false) {
          this.profile = clone(profile);

          this.getApplications();

          this.getMyFavoriteJobPostings();

          this.getMyCallForOpportunities();
        }
      });
  }

  /**
   * Get applications
   */
  getApplications() {
    this.view.isLoading = true;

    this.studentService
      .apiGetApplications(this.profile.id)
      .subscribe((result) => {
        this.view.isLoading = false;

        if (!empty(result)) {
          this.view.data = clone(result);
        }
      });
  }

  /**
   * Open motivation text in modal
   *
   * @param motivation
   */
  openMotivationModal(motivation: string) {
    const data = {
      motivation: motivation,
    };
    this.modal
      .Modal(
        ModalStudentApplicationMotivationComponent,
        "Motivation",
        "",
        data,
        []
      )
      .then((result) => {
        // No specific action
      });
  }

  /**
   * Get favored job postings of current account
   */
  getMyFavoriteJobPostings() {
    this.authService.profile$.subscribe((account) => {
      if (account !== null) {
        const $accountId = account.id;
        this.userService
          .apiGetAccountFavoriteJobpostings($accountId)
          .subscribe((result) => {
            this.viewFavorites.data = clone(result);
          });
      }
    });
  }

  /**
   * Get call for opportunities created by current student
   */
  getMyCallForOpportunities() {
    this.viewAnnouncements.isLoading = true;

    this.callForOpportunityService
      .getCallForOpportunities()
      .pipe(
        catchError(() => {
          this.viewAnnouncements.isLoading = false;
          this.viewAnnouncements.data = [];
          return [];
        })
      )
      .subscribe((response) => {
        this.viewAnnouncements.isLoading = false;

        // Endpoint returns a paged wrapper { data, totalItems, ... }; take the rows.
        const result = response.data ?? [];

        if (!empty(result)) {
          // Filter to only show the current student's call for opportunities
          const filteredResults = result.filter(
            (cfo: any) => cfo.studentProfileId === this.profile.id
          );
          // Sort by startDateTime descending (newest first)
          const sortedResults = sortAnnouncementsNewestFirst(filteredResults);
          this.viewAnnouncements.data = clone(sortedResults);
        } else {
          this.viewAnnouncements.data = [];
        }
      });
  }

  /**
   * Delete call for opportunity
   *
   * @param announcementId
   */
  deleteCallForOpportunity(announcementId: string) {
    if (confirm("Are you sure you want to delete this announcement?")) {
      this.callForOpportunityService
        .deleteCallForOpportunity({ id: announcementId })
        .pipe(
          catchError(() => {
            alert("Failed to delete announcement. Please try again.");
            return [];
          })
        )
        .subscribe(() => {
          // Refresh the announcements list after successful deletion
          this.getMyCallForOpportunities();
        });
    }
  }

  /**
   * Handle job button events
   *
   * @param $event
   */
  handleJobButtonEvent($event: any) {
    if ($event.hasOwnProperty("event")) {
      switch ($event.event) {
        case "deletefavoritejobpostingafter":
          this.getMyFavoriteJobPostings();
          break;
      }
    }
  }
}
